import type {
  ChangeEmailRequest,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  ErrorResponse,
  Locale,
  OkResponse,
  PendingEmailResponse,
  ProfileImageResponse,
  RequestResetRequest,
  RequestVerifyRequest,
  ResetPasswordRequest,
  SetProfileImageRequest,
  TeacherContactResponse,
  TokenRequest,
  TokenResponse,
  RoomListResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UserRole,
} from "@diy-avatar/shared/api";
import type {
  DeviceSignPubkeyResponse,
  E2eeIdentityResponse,
  E2eeRecoveryResponse,
  E2eeStatus,
  SetE2eeIdentityRequest,
  SetE2eeRecoveryRequest,
  SharedKeysResponse,
  StoreSharedKeyRequest,
  StoreSharedKeyResponse,
} from "@diy-avatar/shared/e2ee";
import type {
  E2eeKeylessRequest,
  E2eeKeylessResponse,
} from "@diy-avatar/shared/api";

const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_URL as string | undefined) ??
  "http://localhost:8085";

export interface MeResponse {
  ok: true;
  email: string;
  /** Nickname shown instead of email. */
  display_name: string | null;
  /** Full/real name. Absent on older backends ⇒ treat as null. */
  full_name?: string | null;
  roles: UserRole[];
  /** Preferred UI language. May be absent on older backends ⇒ treat as "de". */
  language?: Locale;
  /** Whether a profile image is set. Absent on older backends ⇒ treat as false. */
  has_profile_image?: boolean;
  /**
   * E2EE scaffolding flag (Phase 5). Absent on older backends ⇒ treat as false.
   * When false the whole E2EE feature is inert; no affordances render.
   */
  e2ee_enabled?: boolean;
  /** Pending (unconfirmed) email change target, or null/absent when none. */
  pending_email?: string | null;
  /** True for the guarded demo principal. Absent on older backends ⇒ false. */
  demo?: boolean;
  /**
   * True when the user must change their password before continuing. Absent on
   * older backends ⇒ treat as false.
   */
  must_change_password?: boolean;
}

export class ApiError extends Error {
  status: number;
  /** The parsed JSON error body (when any), so callers can read structured
   * fields like the E2EE `remediation` block on a blocked join. */
  body: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body ?? null;
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg =
      body &&
      typeof body === "object" &&
      "error" in (body as Record<string, unknown>)
        ? String((body as ErrorResponse).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  return body as T;
}

/**
 * One active session as reported by the backend in a `session_limit` 409 body.
 * All metadata fields may be absent/unknown on the server side and degrade
 * gracefully in the UI (e.g. `ip: null`, `browser: "Unknown"`).
 */
export interface ActiveSession {
  id: string;
  created_at: number | null;
  age_ms: number | null;
  ip: string | null;
  browser: string;
  device: string;
}

/**
 * Parsed JSON error body returned with HTTP 409 when the student hit the
 * max-concurrent-session limit. Lives on {@link ApiError.body}; callers must
 * narrow on `status === 409 && body.error === "session_limit"`.
 */
export interface SessionLimitBody {
  error: "session_limit";
  max_sessions: number;
  active_sessions: ActiveSession[];
}

/**
 * Login. `req.kill_session_ids` (optional) is passed through to let the student
 * free concurrent-session slots on a `session_limit` 409 retry. The backend
 * re-verifies the password before killing the chosen sessions.
 */
export function login(req: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function logout(): Promise<{ ok: true }> {
  return request<{ ok: true }>("/auth/logout", { method: "POST" });
}

export function me(): Promise<MeResponse> {
  return request<MeResponse>("/auth/me", { method: "GET" });
}

// ---- account lifecycle (email verification + password reset) ----

/** (Re)send a verification email. Always resolves 200 (no enumeration). */
export function requestVerify(req: RequestVerifyRequest = {}): Promise<OkResponse> {
  return request<OkResponse>("/auth/request-verify", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Consume a verification token (from the /verify/:token page). */
export function verifyEmail(token: string): Promise<OkResponse> {
  return request<OkResponse>(`/auth/verify/${encodeURIComponent(token)}`, {
    method: "GET",
  });
}

/** Request a password-reset link. Always resolves 200 (no enumeration). */
export function requestReset(req: RequestResetRequest): Promise<OkResponse> {
  return request<OkResponse>("/auth/request-reset", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Set a new password from a reset token (from the /reset/:token page). */
export function resetPassword(
  token: string,
  req: ResetPasswordRequest,
): Promise<OkResponse> {
  return request<OkResponse>(`/auth/reset/${encodeURIComponent(token)}`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function listRooms(): Promise<RoomListResponse> {
  return request<RoomListResponse>("/rooms", { method: "GET" });
}

export function fetchToken(req: TokenRequest): Promise<TokenResponse> {
  return request<TokenResponse>("/livekit/token", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ---- profile image (student avatar) ----

/**
 * Fetch the current user's own profile image (root-relative URL path | null).
 * Pass `size: "full"` for the high-res 16:9 rendition; defaults to the thumbnail.
 */
export async function getMyProfileImage(
  size?: "thumb" | "full",
): Promise<ProfileImageResponse> {
  const path =
    size === "full" ? "/me/profile-image?size=full" : "/me/profile-image";
  const res = await request<ProfileImageResponse>(path, { method: "GET" });
  // Backend now returns a root-relative path (e.g. "/profile-images/<hash>.jpg").
  // Prepend BACKEND_URL so `<img src>` hits the backend origin, not nginx.
  if (typeof res.image === "string" && res.image.startsWith("/")) {
    res.image = `${BACKEND_URL}${res.image}`;
  }
  return res;
}

/**
 * Set (or clear, with null) the current user's profile image data URL. When
 * `imageFull` is passed it is included in the body (the high-res 16:9
 * rendition); omitting the argument omits the field entirely.
 */
export function setMyProfileImage(
  image: SetProfileImageRequest["image"],
  imageFull?: SetProfileImageRequest["imageFull"],
): Promise<ProfileImageResponse> {
  const body: SetProfileImageRequest =
    imageFull === undefined ? { image } : { image, imageFull };
  return request<ProfileImageResponse>("/me/profile-image", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Fetch another participant's profile image by LiveKit identity (email or id).
 * Gated server-side to a shared-school context. Returns null `image` when the
 * student has none set. Pass `size: "full"` for the high-res 16:9 rendition.
 */
export async function getStudentProfileImage(
  idOrEmail: string,
  size?: "thumb" | "full",
): Promise<ProfileImageResponse> {
  const suffix = size === "full" ? "?size=full" : "";
  const res = await request<ProfileImageResponse>(
    `/students/${encodeURIComponent(idOrEmail)}/profile-image${suffix}`,
    { method: "GET" },
  );
  // Backend now returns a root-relative path (e.g. "/profile-images/<hash>.jpg").
  // Prepend BACKEND_URL so `<img src>` hits the backend origin, not nginx.
  if (typeof res.image === "string" && res.image.startsWith("/")) {
    res.image = `${BACKEND_URL}${res.image}`;
  }
  return res;
}

// ----- self-service account management (profile page) -----

/** Update the current user's nickname / full name / language (partial). */
export function updateProfile(
  req: UpdateProfileRequest,
): Promise<UpdateProfileResponse> {
  return request<UpdateProfileResponse>("/me", {
    method: "PATCH",
    body: JSON.stringify(req),
  });
}

/** Change the current user's password (current password required). */
export function changePassword(
  req: ChangePasswordRequest,
): Promise<OkResponse> {
  return request<OkResponse>("/me/change-password", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/**
 * Request an email change. Sends a verification link to the NEW address; the
 * swap applies only after that link is confirmed. Returns the pending target.
 */
export function changeEmail(
  req: ChangeEmailRequest,
): Promise<PendingEmailResponse> {
  return request<PendingEmailResponse>("/me/change-email", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Fetch the current pending (unconfirmed) email-change target, or null. */
export function getPendingEmail(): Promise<PendingEmailResponse> {
  return request<PendingEmailResponse>("/me/pending-email", { method: "GET" });
}

/** Consume an email-change confirmation token (from the /confirm-email page). */
export function confirmEmailChange(token: string): Promise<OkResponse> {
  return request<OkResponse>(
    `/auth/confirm-email/${encodeURIComponent(token)}`,
    { method: "GET" },
  );
}

/**
 * Fetch the assigned teacher's contact email(s) for a room the student belongs
 * to. Pass the room name (e.g. the currently-connected room); omit to fall back
 * to the student's single room.
 */
export function getTeacherContact(
  room?: string,
): Promise<TeacherContactResponse> {
  const suffix = room ? `?room=${encodeURIComponent(room)}` : "";
  return request<TeacherContactResponse>(`/me/teacher-contact${suffix}`, {
    method: "GET",
  });
}

// ----- E2EE scaffolding (Phase 5) -----
// All calls are inert backend-side unless E2EE_ENABLED=1. The /e2ee/status
// probe always responds; the rest 404 when off. Callers gate on the flag.

/** Probe whether E2EE is enabled server-side. Always succeeds. */
export function e2eeStatus(): Promise<{ ok: true } & E2eeStatus> {
  return request<{ ok: true } & E2eeStatus>("/e2ee/status", { method: "GET" });
}

/** Fetch the caller's stored identity (public key + wrapped privkey + kdf). */
export function getE2eeIdentity(): Promise<E2eeIdentityResponse> {
  return request<E2eeIdentityResponse>("/me/e2ee/identity", { method: "GET" });
}

/** Store the caller's identity (opaque, client-wrapped strings). */
export function setE2eeIdentity(
  req: SetE2eeIdentityRequest,
): Promise<E2eeIdentityResponse> {
  return request<E2eeIdentityResponse>("/me/e2ee/identity", {
    method: "PUT",
    body: JSON.stringify(req),
  });
}

/** Load all shared keys for the caller (e.g. on login). */
export function listE2eeKeys(): Promise<SharedKeysResponse> {
  return request<SharedKeysResponse>("/me/e2ee/keys", { method: "GET" });
}

/** Store a shared-key ciphertext (encrypted-to-pubkey, opaque to the server). */
export function storeSharedKey(
  req: StoreSharedKeyRequest,
): Promise<StoreSharedKeyResponse> {
  return request<StoreSharedKeyResponse>("/e2ee/shared-keys", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/** Fetch the caller's E2EE recovery blob (recovery-code-wrapped privkey). */
export function getE2eeRecovery(): Promise<E2eeRecoveryResponse> {
  return request<E2eeRecoveryResponse>("/me/e2ee/recovery", { method: "GET" });
}

/** Store (or clear with both null) the caller's E2EE recovery blob. */
export function setE2eeRecovery(
  req: SetE2eeRecoveryRequest,
): Promise<E2eeRecoveryResponse> {
  return request<E2eeRecoveryResponse>("/me/e2ee/recovery", {
    method: "PUT",
    body: JSON.stringify(req),
  });
}

/**
 * Phase 2: report that this connected, CAPABLE student STILL holds no usable
 * device-scope media key after exhausting its auto-seal heal/retry window while
 * the session wants E2EE active. The "shouldn't happen" loud signal: the server
 * marks this student `keyless=true` for the room (triggering a NON-forced
 * downgrade to plaintext) AND writes a `e2ee_autoseal_failed` audit. Inert (404)
 * when the global E2EE flag is off — callers swallow the failure.
 */
export function reportE2eeKeyless(
  req: E2eeKeylessRequest,
): Promise<E2eeKeylessResponse> {
  return request<E2eeKeylessResponse>("/livekit/e2ee/keyless", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/**
 * Fetch a device's Ed25519 sign public key (for verifying a scanned QR's
 * signature). Gated server-side to students who can view the device's room.
 */
export function getDeviceSignPubkey(
  deviceLabel: string,
): Promise<DeviceSignPubkeyResponse> {
  return request<DeviceSignPubkeyResponse>(
    `/devices/${encodeURIComponent(deviceLabel)}/e2ee/sign-pubkey`,
    { method: "GET" },
  );
}

export const __test = { BACKEND_URL };
